/**
 * Federated Holdings' PRODUCT IDENTITY: the name, the apex it answers on, and the sentences its shell says -- what the
 * Federated application (`apps/federated`, operator decision 2026-09-27) shows around the pages it hosts
 * (`FEDERATED_SHELL_PAGES`). The same overlay tier as `const/kenecting-identity.ts`: every shared component
 * still reads `ACTIVE_BRAND`, and the apex lives here because this product answers on its own, composed through
 * `apexHostnames` / `httpsOrigins` (`const/brand-hostnames.ts`) so nothing here re-spells the grammar.
 */

import { logInTo } from './label-values.ts';
import { productTitleTemplate } from './page-title.ts';
import { apexHostnames, httpsOrigins } from './brand-hostnames.ts';
import type { WranglerEnvKey } from './config-environments.ts';

export const FEDERATED_PRODUCT_NAME = 'Federated Holdings';

/** The product's own apex -- a zone on the fleet's Cloudflare account, attached by the app's manifest at deploy. */
export const FEDERATED_APEX = 'federatedholdings.com';

/** The HOSTNAME the app answers on, per wrangler env block: the fleet's host-app grammar over this apex. */
export const FEDERATED_APP_HOSTNAMES: Readonly<Record<WranglerEnvKey, string>> = apexHostnames(FEDERATED_APEX, null);

/** The public ORIGIN per wrangler env block -- what the manifest binds as `NEXT_PUBLIC_APP_URL`. */
export const FEDERATED_APP_URLS: Readonly<Record<WranglerEnvKey, string>> = httpsOrigins(FEDERATED_APP_HOSTNAMES);

/** The sentences the Federated SHELL says about itself; the app's gate (`SignInGate`) and its menu read them. What it
 *  says about one page -- its title and its loading line -- is that page's row (`FEDERATED_SHELL_PAGES`). */
export const FEDERATED_APP_COPY = {
	/** What the product is for, in one line, so it sits under the sign-in title without wrapping. */
	purpose: 'For the people who run the company.',
	/** The gate's heading: the act, in the verb the button under it carries. */
	signedOutTitle: logInTo(FEDERATED_PRODUCT_NAME),
	/** What a screen reader hears the menu called. */
	menuLabel: `${FEDERATED_PRODUCT_NAME} pages`,
	/** How a tab is named (`productTitleTemplate`): the root layout hands it to Next as its title template, and the home
	 *  page, which Next leaves outside the template of the layout in its own folder, fills it (`titleInTemplate`). */
	titleTemplate: productTitleTemplate(FEDERATED_PRODUCT_NAME),
} as const;
